<template>
  <div class="py-3 py-md-4 pb-5 mb-5">

    <!-- Header -->
    <div class="d-flex align-items-start justify-content-between flex-wrap mb-3" style="gap:10px;">
      <div>
        <h3 class="text-primary font-weight-bold mb-0">BNPL / Debtors</h3>
        <div class="text-secondary small">
          <template v-if="section === 'all'">Everything customers owe you: BNPL, memberships, bookings and invoices</template>
          <template v-else>{{ canViewAll ? 'Money customers owe on BNPL sales' : 'The BNPL sales you made' }}</template>
        </div>
      </div>
      <div v-if="section === 'bnpl'" class="d-flex flex-wrap" style="gap:8px;">
        <button v-if="canRemind && summary && summary.overdue_accounts" class="btn btn-outline-primary" :disabled="bulkReminding" @click="askRemindOverdue">
          <i class="bi bi-chat-dots mr-1"></i>Remind overdue ({{ summary.overdue_accounts }})
        </button>
        <button class="btn btn-outline-secondary" @click="openSettings"><i class="bi bi-gear"></i><span class="d-none d-sm-inline ml-1">Settings</span></button>
      </div>
    </div>

    <!-- One page: BNPL sales, and (when the business has them) every other kind of debt -->
    <ul v-if="hasOtherDebts" class="nav nav-tabs mb-3">
      <li class="nav-item"><a href="#" class="nav-link" :class="{ active: section === 'bnpl' }" @click.prevent="setSection('bnpl')">BNPL</a></li>
      <li class="nav-item"><a href="#" class="nav-link" :class="{ active: section === 'all' }" @click.prevent="setSection('all')">All debtors</a></li>
    </ul>

    <debtors-page v-if="section === 'all'" embedded />

    <div v-else>
    <!-- KPIs (Admin / view-orders / can manage orders only) -->
    <div v-if="canViewAll && summary" class="card mb-3">
      <div class="card-body">
        <div class="row">
          <div class="col-6 col-md mb-3 mb-md-0">
            <div class="small text-secondary">Outstanding</div>
            <div class="h5 font-weight-bold text-dark mb-0">{{ money(summary.outstanding) }}</div>
            <div class="small text-secondary">{{ summary.open_accounts }} open account{{ summary.open_accounts === 1 ? '' : 's' }}</div>
          </div>
          <a href="#" class="col-6 col-md mb-3 mb-md-0 text-decoration-none" @click.prevent="setTab('overdue')">
            <div class="small text-secondary">Overdue</div>
            <div class="h5 font-weight-bold mb-0" :class="summary.overdue_amount > 0 ? 'text-danger' : 'text-dark'">{{ money(summary.overdue_amount) }}</div>
            <div class="small text-secondary">{{ summary.overdue_accounts }} account{{ summary.overdue_accounts === 1 ? '' : 's' }}<template v-if="summary.avg_days_overdue"> · avg {{ summary.avg_days_overdue }} days late</template></div>
          </a>
          <a href="#" class="col-6 col-md mb-3 mb-md-0 text-decoration-none" @click.prevent="setTab('due_soon')">
            <div class="small text-secondary">Due in 7 days</div>
            <div class="h5 font-weight-bold text-dark mb-0">{{ money(summary.due_soon_amount) }}</div>
            <div class="small text-secondary">{{ summary.due_soon_accounts }} account{{ summary.due_soon_accounts === 1 ? '' : 's' }}</div>
          </a>
          <div class="col-6 col-md mb-3 mb-md-0">
            <div class="small text-secondary">Collected this month</div>
            <div class="h5 font-weight-bold text-primary mb-0">{{ money(summary.collected) }}</div>
            <div class="small text-secondary">incl. deposits<template v-if="summary.waived > 0"> · <a href="#" class="text-secondary" @click.prevent="setTab('waived')">{{ money(summary.waived) }} waived</a></template></div>
          </div>
          <div class="col-12 col-md">
            <div class="small text-secondary">Repaid so far</div>
            <div class="h5 font-weight-bold text-dark mb-0">{{ summary.repaid_rate === null ? '—' : summary.repaid_rate + '%' }}</div>
            <div class="progress mt-1" style="height:5px;"><div class="progress-bar bg-primary" :style="{ width: (summary.repaid_rate || 0) + '%' }"></div></div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="canViewAll && summaryError" class="alert alert-danger">{{ summaryError }}</div>

    <!-- Tabs -->
    <ul class="nav nav-pills flex-nowrap mb-3" style="gap:4px;">
      <li v-for="t in mainTabs" :key="t.key" class="nav-item">
        <a href="#" class="nav-link text-nowrap" :class="{ active: tab === t.key }" @click.prevent="setTab(t.key)">{{ t.label }}</a>
      </li>
      <li class="nav-item dropdown">
        <a href="#" class="nav-link text-nowrap" :class="{ active: moreTab }" data-toggle="dropdown" aria-expanded="false" aria-label="More views">
          <template v-if="moreTab">{{ moreTab.label }} </template><i class="bi bi-three-dots-vertical"></i>
          <span v-if="!moreTab && overdueCount" class="badge badge-danger ml-1">{{ overdueCount }}</span>
        </a>
        <div class="dropdown-menu dropdown-menu-right">
          <a v-for="t in moreTabs" :key="t.key" href="#" class="dropdown-item d-flex justify-content-between align-items-center" :class="{ active: tab === t.key }" @click.prevent="setTab(t.key)">
            {{ t.label }}
            <span v-if="t.count" class="badge ml-3" :class="t.key === 'overdue' ? 'badge-danger' : 'badge-secondary'">{{ t.count }}</span>
          </a>
        </div>
      </li>
    </ul>

    <!-- Filters -->
    <div class="card mb-3">
      <div class="card-body pb-2">
        <div class="form-row">
          <div class="form-group col-12 col-lg-4">
            <div class="input-group">
              <div class="input-group-prepend"><span class="input-group-text"><i class="bi bi-search"></i></span></div>
              <input v-model="filters.q" type="search" class="form-control" placeholder="Customer, phone or order #" aria-label="Search" @input="debouncedReload">
            </div>
          </div>
          <div class="form-group col-12 col-sm-6 col-lg-3">
            <date-range-picker v-model="saleRange" :auto-apply="true" :ranges="false" :append-to-body="true" opens="right" class="w-100" @update="onRange('sale')">
              <template #input="picker">
                <span v-if="picker.startDate">{{ tab === 'waived' ? 'Waived' : 'Sold' }} {{ fmtShort(picker.startDate) }} - {{ fmtShort(picker.endDate) }}</span>
                <span v-else class="text-secondary"><i class="bi bi-calendar3 mr-1"></i>{{ tab === 'waived' ? 'Waived date' : 'Sale date' }}</span>
              </template>
            </date-range-picker>
          </div>
          <div class="form-group col-12 col-sm-6 col-lg-3">
            <date-range-picker v-model="dueRange" :auto-apply="true" :ranges="false" :append-to-body="true" opens="left" class="w-100" @update="onRange('due')">
              <template #input="picker">
                <span v-if="picker.startDate">Due {{ fmtShort(picker.startDate) }} - {{ fmtShort(picker.endDate) }}</span>
                <span v-else class="text-secondary"><i class="bi bi-calendar-event mr-1"></i>Due date</span>
              </template>
            </date-range-picker>
          </div>
          <div class="form-group col-12 col-lg-2">
            <button class="btn btn-outline-secondary btn-block" @click="showMoreFilters = !showMoreFilters">
              <i class="bi bi-sliders mr-1"></i>More<span v-if="extraFilterCount" class="badge badge-primary ml-1">{{ extraFilterCount }}</span>
            </button>
          </div>
        </div>
        <div v-if="customerChip && !statement" class="mb-2">
          <span class="badge badge-dark p-2">Customer: {{ customerChip }} <a href="#" class="text-white ml-1" aria-label="Clear customer filter" @click.prevent="clearCustomer"><i class="bi bi-x"></i></a></span>
        </div>
        <div v-if="showMoreFilters" class="form-row">
          <div v-if="canViewAll && summary && summary.staff" class="form-group col-12 col-sm-6 col-lg-3">
            <select v-model="filters.staff_id" class="custom-select" aria-label="Staff" @change="reload">
              <option value="">All staff</option>
              <option v-for="st in summary.staff" :key="st.staff_id" :value="st.staff_id">{{ st.name }}</option>
            </select>
          </div>
          <div class="form-group col-6 col-lg-2">
            <input v-model="filters.min_balance" type="number" min="0" class="form-control" placeholder="Min balance" aria-label="Minimum balance" @change="reload">
          </div>
          <div class="form-group col-6 col-lg-2">
            <input v-model="filters.max_balance" type="number" min="0" class="form-control" placeholder="Max balance" aria-label="Maximum balance" @change="reload">
          </div>
          <div class="form-group col-12 col-sm-6 col-lg-3">
            <select v-model="sortKey" class="custom-select" aria-label="Sort" @change="reload">
              <option value="">Sort: needs attention first</option>
              <option value="days_overdue:desc">Most days overdue</option>
              <option value="balance:desc">Highest balance</option>
              <option value="due_date:asc">Due date (soonest)</option>
              <option value="created_at:desc">Newest sale</option>
              <option value="customer:asc">Customer A-Z</option>
              <option v-if="sortKey && !baseSortKeys.includes(sortKey)" :value="sortKey">Sorted by column</option>
            </select>
          </div>
          <div class="form-group col-12 col-lg-2 d-flex align-items-center">
            <button class="btn btn-link text-secondary px-0" @click="resetFilters"><i class="bi bi-x-circle mr-1"></i>Reset filters</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Customer statement (when one customer is opened) -->
    <div v-if="statement" class="card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start flex-wrap" style="gap:10px;">
          <div style="min-width:0;">
            <div class="small font-weight-bold text-uppercase text-primary">BNPL statement</div>
            <h5 class="font-weight-bold text-dark mb-0 text-truncate">{{ statement.customer.name || 'Customer' }}</h5>
            <div class="small text-secondary">
              {{ statement.customer.phone }}<template v-if="statement.customer.phone"> · </template>{{ statement.totals.sales }} sale{{ statement.totals.sales === 1 ? '' : 's' }}<template v-if="statement.totals.unpaid_sales">, {{ statement.totals.unpaid_sales }} unpaid</template>
            </div>
          </div>
          <div class="d-flex flex-wrap" style="gap:8px;">
            <button v-if="statement.totals.balance > 0 && canCollect" class="btn btn-primary" @click="openSettle({ customer_id: statement.customer.id, name: statement.customer.name, phone: statement.customer.phone })">
              <i class="bi bi-cash-coin mr-1"></i>Receive payment
            </button>
            <a v-if="statement.customer.id" :href="'/customer-statement/' + statement.customer.id" target="_blank" rel="noopener" class="btn btn-outline-secondary">
              <i class="bi bi-file-earmark-text mr-1"></i>Full statement
            </a>
            <button class="btn btn-outline-secondary" aria-label="Close statement" @click="clearCustomer"><i class="bi bi-x-lg"></i></button>
          </div>
        </div>

        <div class="row mt-3" style="row-gap:14px;">
          <div class="col-6 col-md">
            <div class="text-secondary small">Total</div>
            <div class="h5 font-weight-bold text-dark mb-0">{{ money(statement.totals.total) }}</div>
          </div>
          <div class="col-6 col-md">
            <div class="text-secondary small">Paid</div>
            <div class="h5 font-weight-bold text-dark mb-0">{{ money(statement.totals.paid) }}</div>
          </div>
          <div class="col-6 col-md">
            <div class="text-secondary small">Balance</div>
            <div class="h5 font-weight-bold mb-0" :class="statement.totals.balance > 0 ? 'text-danger' : 'text-dark'">{{ money(statement.totals.balance) }}</div>
          </div>
          <div class="col-6 col-md">
            <div class="text-secondary small">Overdue</div>
            <div class="h5 font-weight-bold mb-0" :class="statement.totals.overdue > 0 ? 'text-danger' : 'text-dark'">{{ money(statement.totals.overdue) }}</div>
            <span v-if="statement.totals.max_days_overdue" class="badge badge-danger">{{ statement.totals.max_days_overdue }} days late</span>
            <span v-else-if="statement.totals.next_due" class="badge badge-secondary">Next due {{ fmtShort(statement.totals.next_due) }}</span>
          </div>
          <div v-if="statement.totals.waived > 0" class="col-6 col-md">
            <div class="text-secondary small">Waived</div>
            <div class="h5 font-weight-bold text-dark mb-0">{{ money(statement.totals.waived) }}</div>
          </div>
          <div v-if="statement.wallet_credit > 0" class="col-6 col-md">
            <div class="text-secondary small">Wallet credit</div>
            <div class="h5 font-weight-bold text-primary mb-0">{{ money(statement.wallet_credit) }}</div>
          </div>
        </div>

        <div v-if="statement.totals.total > 0" class="mt-3">
          <div class="progress" style="height:8px;">
            <div class="progress-bar bg-primary" :style="{ width: statementPaidPct + '%' }"></div>
          </div>
          <div class="small text-secondary mt-1">{{ statementPaidPct }}% paid<template v-if="statement.totals.balance <= 0"> · fully cleared</template></div>
        </div>

        <div class="mt-3">
          <a href="#" class="small font-weight-bold" @click.prevent="statementPaymentsOpen = !statementPaymentsOpen">
            <i class="bi mr-1" :class="statementPaymentsOpen ? 'bi-chevron-down' : 'bi-chevron-right'"></i>Payment history ({{ statement.payments.length }})
          </a>
          <div v-if="statementPaymentsOpen" class="table-responsive mt-2">
            <div v-if="!statement.payments.length" class="text-secondary small">No payments yet.</div>
            <table v-else class="table table-hover mb-0">
              <thead><tr><th>Date</th><th>Sale</th><th>Method</th><th class="text-right">Amount</th></tr></thead>
              <tbody>
                <tr v-for="p in statement.payments" :key="p.id" style="cursor:pointer;" @click="openDetail({ id: p.account_id })">
                  <td class="text-nowrap">{{ fmtDate(p.paid_at) }}</td>
                  <td class="text-nowrap">{{ p.order_number }}</td>
                  <td>{{ p.method }}</td>
                  <td class="text-right text-nowrap">{{ money(p.amount) }}</td>
                </tr>
              </tbody>
              <tfoot><tr><td colspan="3" class="font-weight-bold">Total received</td><td class="text-right font-weight-bold text-nowrap">{{ money(statementPaymentsTotal) }}</td></tr></tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Waived -->
    <template v-if="tab === 'waived'">
      <div v-if="loading" class="text-center py-5"><div class="spinner-border text-primary"></div></div>
      <div v-else-if="error" class="alert alert-danger">{{ error }} <a href="#" @click.prevent="reload">Retry</a></div>
      <div v-else-if="!waivers.length" class="card card-body text-center text-secondary py-5">No balances waived{{ filters.date_from ? ' in these dates' : '' }}.</div>
      <template v-else>
        <div class="card">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead class="thead-light"><tr><th>Date</th><th>Customer</th><th>Order</th><th>Reason</th><th>By</th><th class="text-right">Waived</th></tr></thead>
              <tbody>
                <tr v-for="w in waivers" :key="w.id" style="cursor:pointer;" @click="openDetail({ id: w.bnpl_id })">
                  <td class="text-nowrap">{{ fmtDateTime(w.at) }}</td>
                  <td><div class="text-dark">{{ w.customer.name || 'Customer' }}</div><div class="small text-secondary">{{ w.customer.phone }}</div></td>
                  <td class="text-nowrap">{{ w.order_number }}</td>
                  <td class="small">{{ w.reason }}</td>
                  <td class="small text-secondary">{{ w.by || '—' }}</td>
                  <td class="text-right font-weight-bold text-dark text-nowrap">{{ money(w.amount) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr><td colspan="5" class="font-weight-bold">Total waived{{ filters.date_from ? ' in these dates' : '' }}</td><td class="text-right font-weight-bold text-nowrap">{{ money(waiverMeta.amount) }}</td></tr>
              </tfoot>
            </table>
          </div>
        </div>
        <div class="d-flex align-items-center justify-content-between flex-wrap mt-3" style="gap:10px;">
          <span class="text-secondary small">{{ waiverMeta.total }} waiver{{ waiverMeta.total === 1 ? '' : 's' }}</span>
          <b-pagination v-if="waiverMeta.last_page > 1" v-model="page" :total-rows="waiverMeta.total" :per-page="waiverMeta.per_page" class="mb-0" @change="goPage" />
        </div>
      </template>
    </template>

    <!-- By customer -->
    <template v-else-if="tab === 'customers'">
      <div v-if="loading" class="text-center py-5"><div class="spinner-border text-primary"></div></div>
      <div v-else-if="error" class="alert alert-danger">{{ error }} <a href="#" @click.prevent="reload">Retry</a></div>
      <div v-else-if="!customers.length" class="text-center text-secondary py-5"><i class="bi bi-people d-block mb-2" style="font-size:2rem;"></i>No customers match.</div>
      <div v-else class="card">
        <div v-for="c in customers" :key="c.customer_id" class="d-flex align-items-center justify-content-between flex-wrap px-3 py-3 border-bottom" style="gap:10px;">
          <a href="#" class="text-decoration-none" style="min-width:0;" @click.prevent="filterCustomer(c)">
            <div class="font-weight-bold text-dark text-truncate">{{ c.name || 'Customer' }}</div>
            <div class="text-secondary small">{{ c.phone }} · {{ c.accounts }} sale{{ c.accounts === 1 ? '' : 's' }}<template v-if="c.next_due"> · next due {{ fmtDate(c.next_due) }}</template></div>
          </a>
          <div class="d-flex align-items-center" style="gap:16px;">
            <div class="text-right">
              <div class="font-weight-bold" :class="c.balance > 0 ? 'text-danger' : 'text-dark'">{{ money(c.balance) }}</div>
              <div class="text-secondary small">of {{ money(c.total) }}<template v-if="c.overdue > 0"> · {{ money(c.overdue) }} overdue</template></div>
            </div>
            <div class="dropdown d-inline-block">
                    <a href="#" data-toggle="dropdown" data-boundary="viewport" aria-expanded="false" class="btn btn-link text-secondary px-2 py-0" aria-label="Actions">
                      <i class="bi bi-three-dots-vertical"></i>
                    </a>
                    <div class="dropdown-menu dropdown-menu-right">
                      <template v-for="(act, i) in customerActions(c)">
                        <div v-if="act.divider" :key="'d' + i" class="dropdown-divider"></div>
                        <h6 v-else-if="act.header" :key="'h' + i" class="dropdown-header">{{ act.header }}</h6>
                        <a v-else :key="'a' + i" :href="act.href || '#'" :target="act.target || null" :rel="act.target ? 'noopener' : null"
                           class="dropdown-item" :class="{ 'text-danger': act.danger }" @click="runAction(act, $event)">
                          <i class="bi mr-2" :class="act.icon"></i>{{ act.label }}
                        </a>
                      </template>
                    </div>
                  </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Accounts -->
    <template v-else>
      <div v-if="selected.length" class="alert alert-dark d-flex align-items-center justify-content-between flex-wrap" style="gap:10px;position:sticky;top:8px;z-index:5;">
        <span>{{ selected.length }} selected · {{ money(selectedBalance) }} owed<template v-if="!selectedOneCustomer"> · choose one customer to receive</template></span>
        <div class="d-flex" style="gap:8px;">
          <button class="btn btn-primary btn-sm" :disabled="!selectedOneCustomer" :title="selectedOneCustomer ? '' : 'Choose sales of one customer'" @click="openReceiveSelected">
            <i class="bi bi-cash-coin mr-1"></i>Receive payment
          </button>
          <button v-if="canWaive" class="btn btn-outline-light btn-sm" @click="openWaive(selectedAccounts)">
            <i class="bi bi-x-octagon mr-1"></i>Waive
          </button>
          <button v-if="canRemind" class="btn btn-outline-light btn-sm" :disabled="bulkReminding" @click="remindSelected">
            <span v-if="bulkReminding" class="spinner-border spinner-border-sm mr-1"></span>Send reminders
          </button>
          <button class="btn btn-outline-light btn-sm" @click="selected = []">Clear</button>
        </div>
      </div>

      <div v-if="loading && !accounts.length" class="card">
        <div v-for="n in 5" :key="n" class="px-3 py-3 border-bottom"><div class="bg-light rounded" style="height:18px;width:60%;"></div><div class="bg-light rounded mt-2" style="height:12px;width:35%;"></div></div>
      </div>
      <div v-else-if="error" class="alert alert-danger">{{ error }} <a href="#" @click.prevent="reload">Retry</a></div>
      <div v-else-if="!accounts.length" class="card card-body text-center text-secondary py-5">
        <i class="bi d-block mb-2" :class="tab === 'overdue' ? 'bi-emoji-smile' : 'bi-inbox'" style="font-size:2rem;"></i>
        {{ emptyText }}
      </div>

      <template v-else>
        <!-- Desktop table -->
        <div class="card d-none d-md-block">
          <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="thead-light">
              <tr>
                <th style="width:36px;"><input type="checkbox" :checked="allSelected" @change="toggleAll" aria-label="Select all"></th>
                <th v-for="col in accountColumns" :key="col.key" class="text-nowrap" :class="col.class" style="cursor:pointer;user-select:none;" :aria-sort="sortDirFor(col.sort) ? (sortDirFor(col.sort) === 'asc' ? 'ascending' : 'descending') : 'none'" @click="sortBy(col)">
                  {{ col.label }}
                  <i class="bi ml-1" :class="sortDirFor(col.sort) ? (sortDirFor(col.sort) === 'asc' ? 'bi-arrow-up text-primary' : 'bi-arrow-down text-primary') : 'bi-arrow-down-up text-secondary'" style="font-size:11px;"></i>
                </th>
                <th class="text-right" style="width:56px;"><span class="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in accounts" :key="a.id" style="cursor:pointer;" @click="onRowClick(a, $event)">
                <td @click.stop><input v-if="a.balance > 0" v-model="selected" type="checkbox" :value="a.id" :aria-label="'Select ' + a.customer.name"></td>
                <td class="text-nowrap">
                  <div class="text-dark">{{ fmtDate(a.sold_at) }}</div>
                  <div class="text-secondary small">Due {{ a.due_date ? fmtDate(a.due_date) : '—' }}</div>
                  <span class="badge" :class="statusClass(a)">{{ statusLabel(a) }}</span>
                </td>
                <td>
                  <div class="font-weight-bold text-dark">{{ a.customer.name || 'Customer' }}</div>
                  <div class="text-secondary small">{{ a.customer.phone }}</div>
                </td>
                <td class="text-nowrap text-dark">{{ a.order_number }}</td>
                <td class="text-right">
                  <div class="font-weight-bold" :class="a.balance > 0 ? 'text-danger' : 'text-dark'">{{ money(a.balance) }}</div>
                  <div class="text-secondary small">of {{ money(a.total) }}</div>
                </td>
                <td v-if="canViewAll" class="text-secondary small">{{ a.staff.name || '—' }}</td>
                <td class="text-right text-nowrap">
                  <div class="dropdown d-inline-block">
                    <a href="#" data-toggle="dropdown" data-boundary="viewport" aria-expanded="false" class="btn btn-link text-secondary px-2 py-0" aria-label="Actions">
                      <i class="bi bi-three-dots-vertical"></i>
                    </a>
                    <div class="dropdown-menu dropdown-menu-right">
                      <template v-for="(act, i) in accountActions(a)">
                        <div v-if="act.divider" :key="'d' + i" class="dropdown-divider"></div>
                        <h6 v-else-if="act.header" :key="'h' + i" class="dropdown-header">{{ act.header }}</h6>
                        <a v-else :key="'a' + i" :href="act.href || '#'" :target="act.target || null" :rel="act.target ? 'noopener' : null"
                           class="dropdown-item" :class="{ 'text-danger': act.danger }" @click="runAction(act, $event)">
                          <i class="bi mr-2" :class="act.icon"></i>{{ act.label }}
                        </a>
                      </template>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>

        <!-- Mobile cards -->
        <div class="d-md-none">
          <div v-for="a in accounts" :key="'m' + a.id" class="card card-body mb-2 p-3" style="cursor:pointer;" @click="onRowClick(a, $event)">
            <div class="d-flex justify-content-between" style="gap:10px;">
              <div style="min-width:0;">
                <div class="font-weight-bold text-dark text-truncate">{{ a.customer.name || 'Customer' }}</div>
                <div class="text-secondary small">{{ a.order_number }} · {{ fmtDate(a.sold_at) }}</div>
                <div class="text-secondary small">Due {{ a.due_date ? fmtDate(a.due_date) : '—' }}</div>
                <span class="badge mt-1" :class="statusClass(a)">{{ statusLabel(a) }}</span>
              </div>
              <div class="d-flex align-items-start">
                <div class="text-right">
                  <div class="font-weight-bold" :class="a.balance > 0 ? 'text-danger' : 'text-dark'">{{ money(a.balance) }}</div>
                  <div class="text-secondary small">of {{ money(a.total) }}</div>
                </div>
                <div class="dropdown d-inline-block">
                    <a href="#" data-toggle="dropdown" data-boundary="viewport" aria-expanded="false" class="btn btn-link text-secondary px-2 py-0" aria-label="Actions">
                      <i class="bi bi-three-dots-vertical"></i>
                    </a>
                    <div class="dropdown-menu dropdown-menu-right">
                      <template v-for="(act, i) in accountActions(a)">
                        <div v-if="act.divider" :key="'d' + i" class="dropdown-divider"></div>
                        <h6 v-else-if="act.header" :key="'h' + i" class="dropdown-header">{{ act.header }}</h6>
                        <a v-else :key="'a' + i" :href="act.href || '#'" :target="act.target || null" :rel="act.target ? 'noopener' : null"
                           class="dropdown-item" :class="{ 'text-danger': act.danger }" @click="runAction(act, $event)">
                          <i class="bi mr-2" :class="act.icon"></i>{{ act.label }}
                        </a>
                      </template>
                    </div>
                  </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex align-items-center justify-content-between flex-wrap mt-3" style="gap:10px;">
          <span class="text-secondary small">{{ meta.total }} account{{ meta.total === 1 ? '' : 's' }}</span>
          <b-pagination v-if="meta.last_page > 1" v-model="page" :total-rows="meta.total" :per-page="meta.per_page" class="mb-0" @change="goPage" />
        </div>
      </template>
    </template>

    </div>

    <!-- Account detail -->
    <b-sidebar id="bnpl-detail" v-model="detailOpen" right backdrop shadow width="480px" no-header body-class="p-0" @hidden="detail = null">
      <div v-if="detailLoading" class="text-center py-5"><div class="spinner-border text-primary"></div></div>
      <div v-else-if="detail" class="p-3 p-md-4">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <div style="min-width:0;">
            <div class="text-secondary small">{{ detail.account.order_number }} · sold {{ fmtDate(detail.account.sold_at) }}</div>
            <h5 class="font-weight-bold text-dark mb-1 text-truncate">{{ detail.account.customer.name || 'Customer' }}</h5>
            <span class="badge" :class="statusClass(detail.account)">{{ statusLabel(detail.account) }}</span>
          </div>
          <button class="btn btn-link text-secondary p-0" aria-label="Close" @click="detailOpen = false"><i class="bi bi-x-lg"></i></button>
        </div>

        <div class="bg-light rounded p-3 mb-3">
          <div class="d-flex justify-content-between align-items-end">
            <div>
              <div class="small text-secondary">Balance</div>
              <div class="font-weight-bold" :class="detail.account.balance > 0 ? 'text-danger' : 'text-dark'" style="font-size:26px;line-height:1.1;">{{ money(detail.account.balance) }}</div>
            </div>
            <div class="text-right small text-secondary">
              Paid {{ money(detail.account.paid) }}<br>of {{ money(detail.account.total) }}
            </div>
          </div>
          <div class="progress mt-2" style="height:6px;"><div class="progress-bar bg-primary" :style="{ width: paidPct(detail.account) + '%' }"></div></div>
          <div class="small text-secondary mt-2">
            <template v-if="detail.account.due_date">Due {{ fmtDate(detail.account.due_date) }}<template v-if="detail.account.days_overdue"> · <span class="text-danger">{{ detail.account.days_overdue }} days late</span></template></template>
            <template v-else>No due date</template>
            <template v-if="detail.account.deposit > 0"> · deposit {{ money(detail.account.deposit) }}</template>
          </div>
        </div>

        <div class="d-flex flex-wrap mb-3" style="gap:8px;">
          <button v-if="detail.can.collect && detail.account.balance > 0" class="btn btn-primary flex-fill" @click="openPay(detail.account)"><i class="bi bi-cash-coin mr-1"></i>Record payment</button>
          <a :href="orderUrl(detail.account)" class="btn btn-outline-secondary flex-fill"><i class="bi bi-receipt mr-1"></i>View order</a>
          <div class="dropdown flex-fill">
                    <a href="#" data-toggle="dropdown" data-boundary="viewport" aria-expanded="false" class="btn btn-outline-secondary btn-block" aria-label="More actions">
                      More <i class="bi bi-three-dots-vertical ml-1"></i>
                    </a>
                    <div class="dropdown-menu dropdown-menu-right">
                      <template v-for="(act, i) in accountActions(detail.account, true)">
                        <div v-if="act.divider" :key="'d' + i" class="dropdown-divider"></div>
                        <h6 v-else-if="act.header" :key="'h' + i" class="dropdown-header">{{ act.header }}</h6>
                        <a v-else :key="'a' + i" :href="act.href || '#'" :target="act.target || null" :rel="act.target ? 'noopener' : null"
                           class="dropdown-item" :class="{ 'text-danger': act.danger }" @click="runAction(act, $event)">
                          <i class="bi mr-2" :class="act.icon"></i>{{ act.label }}
                        </a>
                      </template>
                    </div>
                  </div>
        </div>

        <div class="small font-weight-bold text-uppercase text-primary mb-2">Items</div>
        <div v-for="(i, idx) in detail.items" :key="'i' + idx" class="d-flex justify-content-between py-1 small">
          <span class="text-dark">{{ i.name }} <span class="text-secondary">× {{ qty(i.qty) }}</span></span>
          <span class="text-dark">{{ money(i.total) }}</span>
        </div>
        <div v-if="!detail.items.length" class="text-secondary small">No item lines.</div>

        <div class="small font-weight-bold text-uppercase text-primary mb-2 mt-3">Payments</div>
        <div v-for="p in detail.payments" :key="p.id" class="d-flex justify-content-between align-items-center py-2 border-bottom small">
          <div>
            <div class="text-dark">{{ money(p.amount) }} <span class="text-secondary">· {{ p.method }}</span></div>
            <div class="text-secondary">{{ fmtDateTime(p.paid_at) }}</div>
          </div>
          <button v-if="detail.can.reverse && p.split_id" class="btn btn-link btn-sm text-danger p-0" @click="askReverse(p)">Reverse</button>
        </div>
        <div v-if="!detail.payments.length" class="text-secondary small">No payments yet.</div>

        <template v-if="detail.waivers && detail.waivers.length">
          <div class="small font-weight-bold text-uppercase text-primary mb-2 mt-3">Waived</div>
          <div v-for="w in detail.waivers" :key="w.id" class="py-2 border-bottom small">
            <div class="d-flex justify-content-between"><span class="text-dark">{{ money(w.amount) }}</span><span class="text-secondary">{{ fmtDateTime(w.at) }}</span></div>
            <div class="text-secondary">{{ w.reason }} · {{ w.by }}</div>
          </div>
        </template>
        <div v-if="detail.wallet_credit > 0" class="alert alert-secondary small mt-3 mb-0">
          {{ detail.account.customer.name }} has <strong>{{ money(detail.wallet_credit) }}</strong> wallet credit (from overpayments or top-ups).
        </div>

        <div class="small font-weight-bold text-uppercase text-primary mb-2 mt-3">Reminders</div>
        <div v-for="r in detail.reminders" :key="r.id" class="py-2 border-bottom small">
          <div class="d-flex justify-content-between">
            <span :class="r.status === 'sent' ? 'text-dark' : 'text-danger'">{{ r.status === 'sent' ? 'Sent' : 'Failed' }} · {{ r.phone }}</span>
            <span class="text-secondary">{{ fmtDateTime(r.created_at) }}</span>
          </div>
          <div v-if="r.source" class="text-secondary">{{ r.source === 'auto' ? 'Automatic' : (r.source === 'bulk' ? 'Bulk' : 'Manual') }}<span v-if="r.by"> · by {{ r.by }}</span></div>
          <div class="text-secondary">{{ r.message }}</div>
        </div>
        <div v-if="!detail.reminders.length" class="text-secondary small">No reminders sent.</div>


      </div>
    </b-sidebar>

    <!-- Record payment -->
    <b-modal v-model="pay.show" title="Record payment" centered ok-title="Record payment" :ok-disabled="pay.busy" :busy="pay.busy" cancel-title="Back" @ok.prevent="submitPay">
      <template v-if="pay.account">
        <p class="text-secondary small mb-3">{{ pay.account.customer.name }} · {{ pay.account.order_number }} · balance <strong class="text-dark">{{ money(pay.account.balance) }}</strong></p>
        <div class="form-group">
          <label for="bnpl-pay-amount">Amount</label>
          <input id="bnpl-pay-amount" v-model.number="pay.amount" type="number" min="1" step="any" class="form-control">
          <div class="mt-1">
            <a href="#" class="small mr-3" @click.prevent="pay.amount = pay.account.balance">Full balance</a>
            <a href="#" class="small" @click.prevent="pay.amount = Math.round(pay.account.balance / 2)">Half</a>
          </div>
        </div>
        <div class="form-group">
          <label>Date received</label>
          <date-range-picker v-if="canBackdate" v-model="pay.date" :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true"
                             :max-date="today" :min-date="pay.account.sold_at ? fmtIso(pay.account.sold_at) : null" class="w-100">
            <template #input="picker">
              <span v-if="picker.startDate">{{ fmtDate(picker.startDate) }}<span v-if="isToday(picker.startDate)" class="text-secondary"> (today)</span></span>
              <span v-else class="text-secondary">Today</span>
            </template>
          </date-range-picker>
          <input v-else type="text" class="form-control" value="Today" readonly aria-label="Date received">
          <small v-if="!canBackdate" class="text-secondary">Only managers can record a payment on an earlier date.</small>
        </div>
        <div class="form-group mb-0">
          <label for="bnpl-pay-method">Paid with</label>
          <select id="bnpl-pay-method" v-model="pay.method" class="custom-select">
            <option :value="null" disabled>Choose payment method</option>
            <option v-for="m in paymentMethods" :key="m.id" :value="m.id">{{ m.payment_method }}</option>
          </select>
        </div>
        <p v-if="pay.amount > pay.account.balance" class="small mt-3 mb-0">
          {{ money(pay.account.balance) }} clears this sale · <strong>{{ money(pay.amount - pay.account.balance) }}</strong> goes to the customer's wallet as credit.
        </p>
        <p v-if="pay.error" class="text-danger small mt-3 mb-0">{{ pay.error }}</p>
      </template>
    </b-modal>

    <!-- Receive from a customer across their BNPL sales (oldest due first) -->
    <b-modal v-model="settle.show" title="Receive payment" centered ok-title="Apply payment" :busy="settle.busy" cancel-title="Back" @ok.prevent="submitSettle">
      <template v-if="settle.customer">
        <p class="text-secondary small mb-3">{{ settle.customer.name }} owes <strong class="text-dark">{{ money(settle.customer.balance) }}</strong> across {{ settle.customer.accounts }} {{ settle.ids ? 'selected ' : '' }}sale(s). The payment clears the oldest due first; anything extra becomes wallet credit.</p>
        <div class="form-group">
          <label for="bnpl-settle-amount">Amount</label>
          <input id="bnpl-settle-amount" v-model.number="settle.amount" type="number" min="1" step="any" class="form-control">
        </div>
        <div class="form-group">
          <label>Date received</label>
          <date-range-picker v-if="canBackdate" v-model="settle.date" :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true"
                             :max-date="today"  class="w-100">
            <template #input="picker">
              <span v-if="picker.startDate">{{ fmtDate(picker.startDate) }}<span v-if="isToday(picker.startDate)" class="text-secondary"> (today)</span></span>
              <span v-else class="text-secondary">Today</span>
            </template>
          </date-range-picker>
          <input v-else type="text" class="form-control" value="Today" readonly aria-label="Date received">
          <small v-if="!canBackdate" class="text-secondary">Only managers can record a payment on an earlier date.</small>
        </div>
        <div class="form-group mb-0">
          <label for="bnpl-settle-method">Paid with</label>
          <select id="bnpl-settle-method" v-model="settle.method" class="custom-select">
            <option :value="null" disabled>Choose payment method</option>
            <option v-for="m in paymentMethods" :key="m.id" :value="m.id">{{ m.payment_method }}</option>
          </select>
        </div>
        <div v-if="settle.amount > 0" class="small mt-3">
          <div v-for="line in settlePlan.lines" :key="line.id" class="d-flex justify-content-between">
            <span>{{ line.order }}<span class="text-secondary"> · {{ line.clears ? 'cleared' : 'part paid' }}</span></span><span>{{ money(line.amount) }}</span>
          </div>
          <div v-if="settlePlan.credit > 0" class="d-flex justify-content-between font-weight-bold">
            <span>To the customer's wallet (credit)</span><span>{{ money(settlePlan.credit) }}</span>
          </div>
        </div>
        <p v-if="settle.error" class="text-danger small mt-3 mb-0">{{ settle.error }}</p>
      </template>
    </b-modal>

    <!-- Reminder preview -->
    <b-modal v-model="remind.show" title="Send reminder" centered ok-title="Send SMS" :busy="remind.busy" cancel-title="Back" @ok.prevent="submitRemind">
      <div v-if="remind.loading" class="text-center py-3"><span class="spinner-border spinner-border-sm text-primary"></span></div>
      <template v-else>
        <p class="text-secondary small mb-2">To {{ remind.phone || 'no phone number' }}<template v-if="remind.account && remind.account.last_reminder_at"> · last reminded {{ fromNow(remind.account.last_reminder_at) }}</template></p>
        <div class="bg-light rounded p-3 small text-dark">{{ remind.message }}</div>
        <p class="text-secondary small mt-2 mb-0">{{ smsNote }} The same customer can't be reminded again within your minimum interval.</p>
        <p v-if="remind.error" class="text-danger small mt-2 mb-0">{{ remind.error }}</p>
      </template>
    </b-modal>

    <!-- Waive (write off) - one sale or several -->
    <b-modal v-model="waiveForm.show" title="Waive balance" centered ok-title="Waive" ok-variant="danger" :busy="waiveForm.busy" cancel-title="Back" @ok.prevent="submitWaive">
      <template v-if="waiveForm.accounts.length">
        <p class="text-secondary small mb-3">
          <template v-if="waiveForm.accounts.length === 1">{{ waiveForm.accounts[0].customer.name }} · {{ waiveForm.accounts[0].order_number }}</template>
          <template v-else>{{ waiveForm.accounts.length }} sales<template v-if="waiveCustomers === 1"> of {{ waiveForm.accounts[0].customer.name }}</template><template v-else> of {{ waiveCustomers }} customers</template></template>
          · owing <strong class="text-dark">{{ money(waiveTotal) }}</strong>
        </p>
        <div class="form-group">
          <div class="custom-control custom-radio">
            <input id="bnpl-waive-full" v-model="waiveForm.mode" type="radio" value="full" class="custom-control-input">
            <label class="custom-control-label" for="bnpl-waive-full">Waive the full {{ waiveForm.accounts.length === 1 ? 'balance' : 'balances' }} ({{ money(waiveTotal) }})</label>
          </div>
          <div class="custom-control custom-radio mt-1">
            <input id="bnpl-waive-amount-mode" v-model="waiveForm.mode" type="radio" value="amount" class="custom-control-input">
            <label class="custom-control-label" for="bnpl-waive-amount-mode">Waive up to an amount<template v-if="waiveForm.accounts.length > 1"> (oldest due first)</template></label>
          </div>
        </div>
        <div v-if="waiveForm.mode === 'amount'" class="form-group">
          <label for="bnpl-waive-amount">Amount to waive</label>
          <input id="bnpl-waive-amount" v-model.number="waiveForm.amount" type="number" min="1" step="any" class="form-control">
        </div>
        <div class="form-group">
          <label for="bnpl-waive-reason">Reason</label>
          <input id="bnpl-waive-reason" v-model="waiveForm.reason" type="text" maxlength="255" class="form-control" placeholder="e.g. Goodwill, unrecoverable, damaged item">
        </div>
        <div v-if="waiveForm.accounts.length > 1" class="small mb-2">
          <div v-for="line in waivePlan" :key="line.id" class="d-flex justify-content-between">
            <span>{{ line.order }}<span class="text-secondary"> · {{ line.customer }}<template v-if="!line.full"> · part</template></span></span><span>{{ money(line.amount) }}</span>
          </div>
        </div>
        <p class="text-secondary small mb-0">The customer won't owe this any more. It's recorded as a bad-debt expense (the sales stay income), with your name and the reason, on each sale.</p>
        <p v-if="waiveForm.error" class="text-danger small mt-2 mb-0">{{ waiveForm.error }}</p>
      </template>
    </b-modal>

    <!-- Confirmations -->
    <b-modal v-model="confirm.show" :title="confirm.title" centered :ok-title="confirm.okTitle" :ok-variant="confirm.variant" :busy="confirm.busy" cancel-title="Back" @ok.prevent="confirm.run">
      <p class="mb-0">{{ confirm.text }}</p>
    </b-modal>

    <!-- Settings -->
    <b-modal v-model="settingsModal.show" title="BNPL settings" size="lg" centered scrollable :ok-title="settingsModal.canEdit ? 'Save settings' : 'Close'" :ok-only="!settingsModal.canEdit" :busy="settingsModal.busy" cancel-title="Back" @ok.prevent="saveSettings">
      <div v-if="!settingsModal.data" class="text-center py-4"><span class="spinner-border text-primary"></span></div>
      <fieldset v-else :disabled="!settingsModal.canEdit">
        <div class="small font-weight-bold text-uppercase text-primary mb-2">Terms</div>
        <div class="row">
          <div class="form-group col-sm-6">
            <label>Default time to pay (days)</label>
            <input v-model.number="settingsModal.data.default_term_days" type="number" min="1" max="365" class="form-control">
            <small class="text-secondary">Prefills the due date at the till.</small>
          </div>
          <div class="form-group col-sm-6">
            <label>Grace period (days)</label>
            <input v-model.number="settingsModal.data.grace_days" type="number" min="0" max="90" class="form-control">
            <small class="text-secondary">Days after the due date before an account counts as overdue.</small>
          </div>
        </div>

        <div class="small font-weight-bold text-uppercase text-primary mb-2 mt-2">Automatic reminders (SMS)</div>
        <div class="custom-control custom-switch mb-3">
          <input id="bnpl-rem-on" v-model="settingsModal.data.reminders_enabled" type="checkbox" class="custom-control-input">
          <label class="custom-control-label" for="bnpl-rem-on">Send reminders automatically every morning</label>
        </div>
        <div class="row">
          <div class="form-group col-sm-6">
            <label>Before the due date (days)</label>
            <input v-model="settingsModal.daysBefore" type="text" class="form-control" placeholder="e.g. 3, 1">
            <small class="text-secondary">Comma-separated. Leave empty for none.</small>
          </div>
          <div class="form-group col-sm-6 d-flex align-items-center">
            <div class="custom-control custom-switch mt-sm-3">
              <input id="bnpl-rem-dueday" v-model="settingsModal.data.remind_on_due_day" type="checkbox" class="custom-control-input">
              <label class="custom-control-label" for="bnpl-rem-dueday">On the due day</label>
            </div>
          </div>
          <div class="form-group col-sm-4">
            <label>When overdue, every (days)</label>
            <input v-model.number="settingsModal.data.overdue_every_days" type="number" min="0" max="90" class="form-control">
            <small class="text-secondary">0 = don't remind once overdue.</small>
          </div>
          <div class="form-group col-sm-4">
            <label>Max automatic per account</label>
            <input v-model.number="settingsModal.data.max_reminders" type="number" min="1" max="30" class="form-control">
          </div>
          <div class="form-group col-sm-4">
            <label>Min gap between any two (hours)</label>
            <input v-model.number="settingsModal.data.min_hours_between" type="number" min="1" max="720" class="form-control">
          </div>
        </div>
        <div class="form-group mb-0">
          <label>Message</label>
          <textarea v-model="settingsModal.data.reminder_template" rows="3" maxlength="480" class="form-control" :placeholder="settingsModal.data.default_template"></textarea>
          <div class="mt-1">
            <span v-for="p in settingsModal.data.placeholders" :key="p" class="badge badge-secondary mr-1" style="cursor:pointer;" @click="insertPlaceholder(p)">{{ p }}</span>
          </div>
          <small class="text-secondary d-block mt-1">Leave empty to use the standard message. {{ smsNote }}</small>
        </div>
      </fieldset>
      <p v-if="settingsModal.error" class="text-danger small mt-3 mb-0">{{ settingsModal.error }}</p>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker'

/**
 * BNPL accounts: summary, actionable queues (overdue / due soon), filters,
 * account detail with payments and reminders, settings.
 * API: /api/bnpl/* (BnplAccountsController) plus the existing bulk settle,
 * reverse and delete endpoints (BNPLContoller).
 */
export default {
  name: 'BnplPage',

  components: { DateRangePicker },

  props: { customerView: { type: [String, Number], default: null } },

  data() {
    return {
      tab: 'all',
      section: new URLSearchParams(window.location.search).get('view') === 'all' ? 'all' : 'bnpl',
      summary: null,
      summaryError: null,
      accounts: [],
      customers: [],
      waivers: [],
      waiverMeta: { current_page: 1, last_page: 1, per_page: 25, total: 0, amount: 0 },
      meta: { current_page: 1, last_page: 1, per_page: 25, total: 0 },
      page: 1,
      loading: false,
      error: null,
      filters: { q: '', staff_id: '', min_balance: '', max_balance: '', date_from: null, date_to: null, due_from: null, due_to: null, customer_id: null },
      sortKey: '',
      showMoreFilters: false,
      customerChip: null,
      statement: null,
      statementPaymentsOpen: false,
      saleRange: { startDate: null, endDate: null },
      dueRange: { startDate: null, endDate: null },
      selected: [],
      bulkReminding: false,
      paymentMethods: [],

      detailOpen: false,
      detailLoading: false,
      detail: null,

      pay: { show: false, account: null, amount: null, method: null, date: { startDate: null, endDate: null }, busy: false, error: null },
      settle: { show: false, customer: null, accounts: [], ids: [], amount: null, method: null, date: { startDate: null, endDate: null }, busy: false, error: null },
      today: moment().format('YYYY-MM-DD'),
      waiveForm: { show: false, accounts: [], mode: 'full', amount: null, reason: '', busy: false, error: null },
      remind: { show: false, account: null, loading: false, message: '', phone: '', busy: false, error: null },
      confirm: { show: false, title: '', text: '', okTitle: 'OK', variant: 'primary', busy: false, run: () => {} },
      settingsModal: { show: false, data: null, daysBefore: '', canEdit: false, busy: false, error: null },
    }
  },

  computed: {
    ...mapGetters(['userDetails']),

    // Memberships, bookings or invoices -> the "All debtors" tab.
    hasOtherDebts() {
      const s = this.userDetails?.company?.companysettings || {}
      return !!(s.has_subscriptions || s.has_reservations || s.has_invoices)
    },
    isAdmin() {
      return (this.userDetails.roles || []).some(r => r.name === 'Admin' || r.name === 'owner')
    },
    // Same rules as BnplService: everyone sees their own BNPL sales and can
    // take repayments on them; these see every account + the figures.
    canViewAll() {
      return this.isAdmin || this.hasPermission('view-orders') || this.hasPermission('can manage orders')
    },
    canCollect() {
      return true
    },
    // Same rule as BnplService::canBackdate.
    canBackdate() {
      return this.isAdmin || this.hasPermission('can manage orders')
    },
    // Same rule as BnplService::canWaive (Admin passes via isAdmin).
    canWaive() {
      return this.isAdmin || this.hasPermission('void-orders')
    },
    waiveTotal() {
      return this.waiveForm.accounts.reduce((t, a) => t + a.balance, 0)
    },
    waiveCustomers() {
      return new Set(this.waiveForm.accounts.map(a => a.customer.id)).size
    },
    // Preview of BnplService::waiveMany (oldest due first).
    waivePlan() {
      const w = this.waiveForm
      const list = w.accounts.slice().sort((x, y) => (x.due_date || '9999').localeCompare(y.due_date || '9999'))
      let left = w.mode === 'amount' ? Number(w.amount || 0) : Infinity
      const out = []
      for (const a of list) {
        if (left <= 0) break
        const amt = Math.min(left, a.balance)
        out.push({ id: a.id, order: a.order_number, customer: a.customer.name, amount: amt, full: amt >= a.balance })
        left -= amt
      }
      return out
    },
    selectedAccounts() {
      return this.accounts.filter(a => this.selected.includes(a.id))
    },
    selectedBalance() {
      return this.selectedAccounts.reduce((t, a) => t + a.balance, 0)
    },
    selectedOneCustomer() {
      const ids = [...new Set(this.selectedAccounts.map(a => a.customer.id))]
      return ids.length === 1
    },
    // Client-side preview of BnplService::receive (oldest due first, extra -> wallet).
    settlePlan() {
      const s = this.settle
      const accounts = (s.accounts || []).slice().sort((x, y) => (x.due_date || '9999') .localeCompare(y.due_date || '9999'))
      let left = Number(s.amount || 0)
      const lines = []
      for (const a of accounts) {
        if (left <= 0) break
        const pay = Math.min(left, a.balance)
        lines.push({ id: a.id, order: a.order_number, amount: pay, clears: pay >= a.balance })
        left -= pay
      }
      return { lines, credit: Math.max(0, Math.round(left * 100) / 100) }
    },
    canRemind() {
      return this.isAdmin || this.hasPermission('can manage orders')
    },
    accountColumns() {
      const cols = [
        { key: 'date', label: 'Date', sort: 'created_at', dir: 'desc' },
        { key: 'customer', label: 'Customer', sort: 'customer', dir: 'asc' },
        { key: 'order', label: 'Order', sort: 'order_number', dir: 'desc' },
        { key: 'balance', label: 'Balance', sort: 'balance', dir: 'desc', class: 'text-right' },
      ]
      if (this.canViewAll) cols.push({ key: 'staff', label: 'Staff', sort: 'staff', dir: 'asc' })
      return cols
    },
    baseSortKeys() {
      return ['days_overdue:desc', 'balance:desc', 'due_date:asc', 'created_at:desc', 'customer:asc']
    },
    mainTabs() {
      return [
        { key: 'all', label: 'All' },
        { key: 'customers', label: 'By customer' },
        { key: 'waived', label: 'Waived' },
      ]
    },
    // Behind the three-dots button next to the main tabs.
    moreTabs() {
      const s = this.summary || {}
      return [
        { key: 'overdue', label: 'Overdue', count: s.overdue_accounts },
        { key: 'due_soon', label: 'Due soon', count: s.due_soon_accounts },
        { key: 'unpaid', label: 'All unpaid', count: s.open_accounts },
        { key: 'paid', label: 'Paid', count: null },
      ]
    },
    moreTab() {
      return this.moreTabs.find(t => t.key === this.tab) || null
    },
    overdueCount() {
      return (this.summary && this.summary.overdue_accounts) || 0
    },

    statementPaidPct() {
      const t = this.statement?.totals
      return t && t.total > 0 ? Math.min(100, Math.round((t.total - t.balance) / t.total * 100)) : 0
    },
    statementPaymentsTotal() {
      return (this.statement?.payments || []).reduce((t, p) => t + p.amount, 0)
    },

    extraFilterCount() {
      return ['staff_id', 'min_balance', 'max_balance'].filter(k => this.filters[k] !== '' && this.filters[k] !== null).length + (this.sortKey ? 1 : 0)
    },
    allSelected() {
      const ids = this.accounts.filter(a => a.balance > 0).map(a => a.id)
      return ids.length > 0 && ids.every(id => this.selected.includes(id))
    },
    // Same rule as SMSController: only messages on the shared sender are billed.
    smsNote() {
      const sender = this.userDetails?.company?.companysettings?.sender_id
      return sender && sender !== 'Dukakit'
        ? `Sent from your sender ID (${sender}) - not counted toward the free 100.`
        : 'Counts toward your 100 free SMS a month (KES 1 each after that).'
    },
    emptyText() {
      return {
        overdue: 'Nothing overdue. Nice.',
        due_soon: 'Nothing due in the next 7 days.',
        unpaid: 'No unpaid BNPL sales.',
        paid: 'No fully paid BNPL sales yet.',
        all: 'No BNPL sales match.',
      }[this.tab] || 'Nothing to show.'
    },
  },

  created() {
    this._t = null
    if (String(this.customerView) === '1') this.tab = 'customers'
  },

  mounted() {
    if (this.canViewAll) this.loadSummary()
    this.reload()
  },

  methods: {
    setSection(section) {
      this.section = section
      try {
        const url = new URL(window.location.href)
        section === 'all' ? url.searchParams.set('view', 'all') : url.searchParams.delete('view')
        window.history.replaceState({}, '', url)
      } catch (e) { /* ignore */ }
    },
    hasPermission(name) {
      return (this.userDetails.permissions || []).some(p => p.name === name)
    },

    params() {
      const [sort, dir] = (this.sortKey || '').split(':')
      const p = { ...this.filters, status: this.tab === 'customers' ? 'unpaid' : this.tab, page: this.page, per_page: 25 }
      if (sort) { p.sort = sort; p.dir = dir }
      Object.keys(p).forEach(k => (p[k] === '' || p[k] === null) && delete p[k])
      return p
    },

    async loadSummary() {
      try {
        const { data } = await axios.get('/api/bnpl/summary')
        this.summary = data
        this.summaryError = null
      } catch (e) {
        this.summaryError = e.response?.status === 403 ? 'You don\'t have access to BNPL balances.' : 'Could not load the BNPL summary.'
      }
    },

    async reload() {
      this.loading = true
      this.error = null
      this.loadStatement()
      try {
        if (this.tab === 'waived') {
          const p = this.params()
          const { data } = await axios.get('/api/bnpl/waivers', { params: { q: p.q, customer_id: p.customer_id, date_from: p.date_from, date_to: p.date_to, page: this.page, per_page: 25 } })
          this.waivers = data.data
          this.waiverMeta = data.meta
          this.page = data.meta.current_page
        } else if (this.tab === 'customers') {
          const { data } = await axios.get('/api/bnpl/customers', { params: this.params() })
          this.customers = data
        } else {
          const { data } = await axios.get('/api/bnpl/accounts', { params: this.params() })
          this.accounts = data.data
          this.meta = data.meta
          this.page = data.meta.current_page
          this.selected = this.selected.filter(id => this.accounts.some(a => a.id === id))
        }
      } catch (e) {
        this.error = e.response?.status === 403 ? 'You don\'t have access to BNPL balances.' : 'Could not load BNPL accounts.'
      } finally {
        this.loading = false
      }
    },

    sortDirFor(field) {
      const [sort, dir] = (this.sortKey || '').split(':')
      return sort === field ? dir : null
    },
    // Header click: first click uses the column's natural direction, then flips.
    sortBy(col) {
      const current = this.sortDirFor(col.sort)
      this.sortKey = col.sort + ':' + (current ? (current === 'asc' ? 'desc' : 'asc') : col.dir)
      this.page = 1
      this.reload()
    },

    async loadStatement() {
      const id = this.filters.customer_id
      if (!id) { this.statement = null; return }
      try {
        const { data } = await axios.get(`/api/bnpl/customers/${id}/statement`)
        if (this.filters.customer_id === id) this.statement = data
      } catch (e) {
        this.statement = null
      }
    },

    debouncedReload() {
      clearTimeout(this._t)
      this._t = setTimeout(() => { this.page = 1; this.reload() }, 300)
    },

    // The orders page opens ?order-number= by FinalOrder id, not the sale number.
    orderUrl(a) {
      return '/orders?order-number=' + encodeURIComponent(a.final_order_id) + '&source=/bnpl'
    },

    setTab(key) {
      this.tab = key
      this.page = 1
      this.selected = []
      this.reload()
    },

    goPage(p) {
      this.page = p
      this.reload()
    },

    onRange(kind) {
      const r = kind === 'sale' ? this.saleRange : this.dueRange
      const from = r.startDate ? moment(r.startDate).format('YYYY-MM-DD') : null
      const to = r.endDate ? moment(r.endDate).format('YYYY-MM-DD') : null
      if (kind === 'sale') { this.filters.date_from = from; this.filters.date_to = to } else { this.filters.due_from = from; this.filters.due_to = to }
      this.page = 1
      this.reload()
    },

    resetFilters() {
      this.filters = { q: '', staff_id: '', min_balance: '', max_balance: '', date_from: null, date_to: null, due_from: null, due_to: null, customer_id: null }
      this.saleRange = { startDate: null, endDate: null }
      this.dueRange = { startDate: null, endDate: null }
      this.sortKey = ''
      this.customerChip = null
      this.page = 1
      this.reload()
    },

    filterCustomer(c) {
      this.filters.customer_id = c.customer_id
      this.customerChip = c.name || c.phone || 'Customer'
      this.setTab('all')
    },
    clearCustomer() {
      this.filters.customer_id = null
      this.customerChip = null
      this.page = 1
      this.reload()
    },

    toggleAll(e) {
      this.selected = e.target.checked ? this.accounts.filter(a => a.balance > 0).map(a => a.id) : []
    },

    async ensureMethods() {
      if (this.paymentMethods.length) return
      try {
        const { data } = await axios.get('/api/payments')
        // Can't repay a BNPL balance "with BNPL".
        this.paymentMethods = (Array.isArray(data) ? data : []).filter(m => m.enabled !== false && !/later|bnpl/i.test(m.payment_method || ''))
      } catch (e) { this.paymentMethods = [] }
    },

    // ── detail ──
    async openDetail(a) {
      this.detailOpen = true
      this.detailLoading = true
      try {
        const { data } = await axios.get(`/api/bnpl/accounts/${a.id}`)
        this.detail = data
      } catch (e) {
        this.detailOpen = false
        this.toast('error', 'Could not open this account.')
      } finally {
        this.detailLoading = false
      }
    },

    refreshAfterChange(id) {
      if (this.canViewAll) this.loadSummary()
      this.reload()
      if (this.detailOpen && this.detail && this.detail.account.id === id) this.openDetail({ id })
    },

    // ── pay ──
    async openPay(a, full = false) {
      await this.ensureMethods()
      this.pay = { show: true, account: a, amount: a.balance, full, date: { startDate: new Date(), endDate: new Date() }, method: this.paymentMethods.length === 1 ? this.paymentMethods[0].id : null, busy: false, error: null }
    },
    async submitPay() {
      const p = this.pay
      if (!(p.amount > 0) || !p.method) { p.error = 'Enter an amount and choose how it was paid.'; return }
      p.busy = true
      p.error = null
      try {
        const { data } = await axios.post(`/api/bnpl/accounts/${p.account.id}/payments`, { amount: p.amount, payment_type_id: p.method, received_on: this.dateParam(p.date) })
        p.show = false
        this.toast('success', (data.paid ? 'Paid in full.' : `Payment recorded. Balance ${this.money(data.balance)}.`) + (data.credited > 0 ? ` ${this.money(data.credited)} went to the customer's wallet as credit.` : ''))
        this.refreshAfterChange(p.account.id)
      } catch (e) {
        p.error = this.errorText(e, 'Could not record the payment.')
      } finally {
        p.busy = false
      }
    },

    // ── settle across a customer's sales (existing endpoint) ──
    // Receive across a customer's unpaid sales (all of them, or the ticked ones).
    async openSettle(c, accounts = null) {
      await this.ensureMethods()
      if (!accounts) {
        try {
          const { data } = await axios.get('/api/bnpl/accounts', { params: { customer_id: c.customer_id, status: 'unpaid', per_page: 100 } })
          accounts = data.data
        } catch (e) { this.toast('error', 'Could not load the customer.'); return }
      }
      const balance = accounts.reduce((t, a) => t + a.balance, 0)
      this.settle = {
        show: true, customer: { ...c, balance, accounts: accounts.length }, accounts, ids: accounts.map(a => a.id),
        amount: balance, date: { startDate: new Date(), endDate: new Date() },
        method: this.paymentMethods.length === 1 ? this.paymentMethods[0].id : null, busy: false, error: null,
      }
    },
    openReceiveSelected() {
      const first = this.selectedAccounts[0]
      this.openSettle({ customer_id: first.customer.id, name: first.customer.name, phone: first.customer.phone }, this.selectedAccounts.filter(a => a.balance > 0))
    },
    // One sale, the ticked ones, or all of a customer's unpaid sales.
    openWaive(accounts) {
      const list = (Array.isArray(accounts) ? accounts : [accounts]).filter(a => a.balance > 0)
      if (!list.length) { this.toast('error', 'Nothing to waive.'); return }
      this.waiveForm = { show: true, accounts: list, mode: 'full', amount: null, reason: '', busy: false, error: null }
    },
    async openWaiveCustomer(c) {
      try {
        const { data } = await axios.get('/api/bnpl/accounts', { params: { customer_id: c.customer_id, status: 'unpaid', per_page: 100 } })
        this.openWaive(data.data)
      } catch (e) { this.toast('error', 'Could not load the customer.') }
    },
    async submitWaive() {
      const w = this.waiveForm
      if (w.mode === 'amount' && !(w.amount > 0)) { w.error = 'Enter the amount to waive.'; return }
      if (w.reason.trim().length < 3) { w.error = 'Add a short reason.'; return }
      w.busy = true
      w.error = null
      try {
        const { data } = await axios.post('/api/bnpl/waive', {
          ids: w.accounts.map(a => a.id), amount: w.mode === 'amount' ? w.amount : null, reason: w.reason.trim(),
        })
        w.show = false
        this.selected = []
        this.toast('success', `${this.money(data.waived)} waived on ${data.sales} sale${data.sales === 1 ? '' : 's'}.`)
        if (this.canViewAll) this.loadSummary()
        this.reload()
        if (this.detailOpen && this.detail) this.openDetail({ id: this.detail.account.id })
      } catch (e) {
        w.error = this.errorText(e, 'Could not waive.')
      } finally {
        w.busy = false
      }
    },
    async submitSettle() {
      const s = this.settle
      if (!(s.amount > 0) || !s.method) { s.error = 'Enter an amount and choose how it was paid.'; return }
      s.busy = true
      s.error = null
      try {
        const { data } = await axios.post('/api/bnpl/receive', {
          ids: s.ids, amount: s.amount, payment_type_id: s.method, received_on: this.dateParam(s.date),
        })
        s.show = false
        this.selected = []
        this.toast('success', 'Payment applied to ' + data.applied.length + ' sale' + (data.applied.length === 1 ? '' : 's') + '.' + (data.credited > 0 ? ` ${this.money(data.credited)} went to the customer's wallet as credit.` : ''))
        if (this.canViewAll) this.loadSummary()
        this.reload()
      } catch (e) {
        s.error = this.errorText(e, 'Could not apply the payment.')
      } finally {
        s.busy = false
      }
    },

    // ── remind ──
    async openRemind(a) {
      this.remind = { show: true, account: a, loading: true, message: '', phone: '', busy: false, error: null }
      try {
        const { data } = await axios.get(`/api/bnpl/accounts/${a.id}/reminder-preview`)
        this.remind.message = data.message
        this.remind.phone = data.phone
      } catch (e) {
        this.remind.error = 'Could not prepare the message.'
      } finally {
        this.remind.loading = false
      }
    },
    async submitRemind() {
      const r = this.remind
      r.busy = true
      r.error = null
      try {
        await axios.post(`/api/bnpl/accounts/${r.account.id}/remind`)
        r.show = false
        this.toast('success', 'Reminder sent.')
        this.refreshAfterChange(r.account.id)
      } catch (e) {
        r.error = e.response?.data?.reason || this.errorText(e, 'Could not send the reminder.')
      } finally {
        r.busy = false
      }
    },
    askRemindOverdue() {
      const n = this.summary.overdue_accounts
      this.ask('Remind overdue customers', `Send an SMS reminder to the ${n} overdue account${n === 1 ? '' : 's'}? Anyone reminded recently is skipped automatically.`, 'Send reminders', 'primary', async () => {
        const ids = []
        let page = 1, last = 1
        do {
          const { data } = await axios.get('/api/bnpl/accounts', { params: { status: 'overdue', per_page: 100, page } })
          data.data.forEach(a => ids.push(a.id))
          last = data.meta.last_page
          page++
        } while (page <= last && ids.length < 200)
        await this.sendMany(ids)
      })
    },
    async remindSelected() {
      await this.sendMany(this.selected)
      this.selected = []
    },
    async sendMany(ids) {
      if (!ids.length) return
      this.bulkReminding = true
      try {
        const { data } = await axios.post('/api/bnpl/remind', { ids })
        this.toast('success', `${data.sent} reminder${data.sent === 1 ? '' : 's'} sent` + (data.skipped.length ? `, ${data.skipped.length} skipped (reminded recently, paid or no phone).` : '.'))
        this.reload()
      } catch (e) {
        this.toast('error', this.errorText(e, 'Could not send reminders.'))
      } finally {
        this.bulkReminding = false
      }
    },

    // ── three-dots menus ──
    accountActions(a, inPanel = false) {
      const unpaid = a.balance > 0
      const phone = (a.customer.phone || '').replace(/[^0-9]/g, '')
      const list = []
      if (!inPanel) list.push({ label: 'View details', icon: 'bi-eye', run: () => this.openDetail(a) })
      if (unpaid && this.canCollect) {
        if (!inPanel) list.push({ label: 'Record payment', icon: 'bi-cash-coin', run: () => this.openPay(a) })
        list.push({ label: 'Mark paid in full', icon: 'bi-check2-circle', run: () => this.openPay(a, true) })
      }
      if (unpaid && this.canWaive) list.push({ label: 'Waive balance', icon: 'bi-x-octagon', run: () => this.openWaive(a) })
      if (unpaid && this.canRemind) list.push({ label: a.last_reminder_at ? `Send reminder (last ${this.fromNow(a.last_reminder_at)})` : 'Send SMS reminder', icon: 'bi-chat-dots', run: () => this.openRemind(a) })
      if (phone) {
        list.push({ divider: true }, { header: 'Contact' })
        list.push({ label: 'Call ' + (a.customer.name || 'customer'), icon: 'bi-telephone', href: 'tel:' + a.customer.phone })
        list.push({ label: 'WhatsApp', icon: 'bi-whatsapp', href: `https://wa.me/${phone}?text=${encodeURIComponent(this.waText(a))}`, target: '_blank' })
        list.push({ label: 'Copy phone number', icon: 'bi-clipboard', run: () => this.copy(a.customer.phone, 'Phone number copied.') })
      }
      list.push({ divider: true }, { header: 'Go to' })
      list.push({ label: 'Original order', icon: 'bi-receipt', href: this.orderUrl(a) })
      if (a.customer.id) {
        list.push({ label: 'Customer profile', icon: 'bi-person', href: '/customer?id=' + a.customer.id })
        list.push({ label: "All this customer's BNPL", icon: 'bi-funnel', run: () => { this.detailOpen = false; this.filterCustomer({ customer_id: a.customer.id, name: a.customer.name, phone: a.customer.phone }) } })
        if (this.canCollect) list.push({ label: "Receive across customer's sales", icon: 'bi-wallet2', run: () => this.openSettleFor(a.customer) })
      }
      list.push({ label: 'Copy order number', icon: 'bi-hash', run: () => this.copy(a.order_number, 'Order number copied.') })
      if (inPanel && this.detail && this.detail.can.delete) {
        list.push({ divider: true }, { label: 'Delete BNPL sale', icon: 'bi-trash', danger: true, run: () => this.askDelete(a) })
      }
      return list
    },
    customerActions(c) {
      const phone = (c.phone || '').replace(/[^0-9]/g, '')
      const list = [{ label: 'View their BNPL sales', icon: 'bi-list-ul', run: () => this.filterCustomer(c) }]
      if (c.balance > 0 && this.canCollect) list.push({ label: 'Receive payment', icon: 'bi-cash-coin', run: () => this.openSettle(c) })
      if (c.balance > 0 && this.canRemind) list.push({ label: 'Remind about all unpaid', icon: 'bi-chat-dots', run: () => this.remindCustomer(c) })
      if (c.balance > 0 && this.canWaive) list.push({ label: 'Waive all unpaid', icon: 'bi-x-octagon', danger: true, run: () => this.openWaiveCustomer(c) })
      if (phone) {
        list.push({ divider: true })
        list.push({ label: 'Call', icon: 'bi-telephone', href: 'tel:' + c.phone })
        list.push({ label: 'WhatsApp', icon: 'bi-whatsapp', href: `https://wa.me/${phone}`, target: '_blank' })
      }
      list.push({ divider: true }, { label: 'Customer profile', icon: 'bi-person', href: '/customer?id=' + c.customer_id })
      return list
    },
    // Row opens the account unless the click was on its checkbox or menu
    // (the menu needs the click to bubble to Bootstrap's document listener).
    onRowClick(a, e) {
      if (e.target.closest('.dropdown, input, a, button')) return
      this.openDetail(a)
    },
    runAction(act, e) {
      if (act.run) { e.preventDefault(); act.run() }
    },
    waText(a) {
      return `Hi ${(a.customer.name || '').split(' ')[0]}, your balance on order ${a.order_number} is ${this.money(a.balance)}` + (a.due_date ? `, due ${this.fmtDate(a.due_date)}.` : '.')
    },
    async copy(text, done) {
      try { await navigator.clipboard.writeText(text); this.toast('success', done) } catch (e) { this.toast('error', 'Could not copy.') }
    },
    openSettleFor(customer) {
      this.detailOpen = false
      this.openSettle({ customer_id: customer.id, name: customer.name, phone: customer.phone })
    },
    async remindCustomer(c) {
      const { data } = await axios.get('/api/bnpl/accounts', { params: { customer_id: c.customer_id, status: 'unpaid', per_page: 100 } })
      await this.sendMany(data.data.map(a => a.id))
    },

    // ── reverse / delete (existing Admin endpoints) ──
    askReverse(p) {
      this.ask('Reverse payment', `Reverse the payment of ${this.money(p.amount)}? The balance goes back up by that amount.`, 'Reverse', 'danger', async () => {
        await axios.post('/api/reverse-bnpl-transaction', { requestData: { data: { orders: [{ id: p.split_id }] } } })
        this.toast('success', 'Payment reversed.')
        this.refreshAfterChange(this.detail.account.id)
      })
    },
    askDelete(a) {
      this.ask('Delete BNPL sale', `Delete ${a.order_number} for ${a.customer.name}? This deletes the order too and can't be undone here.`, 'Delete', 'danger', async () => {
        await axios.delete('/api/delet-bnpl', { params: { id: a.id } })
        this.detailOpen = false
        this.toast('success', 'BNPL sale deleted.')
        if (this.canViewAll) this.loadSummary()
        this.reload()
      })
    },
    ask(title, text, okTitle, variant, fn) {
      this.confirm = {
        show: true, title, text, okTitle, variant, busy: false,
        run: async () => {
          this.confirm.busy = true
          try { await fn(); this.confirm.show = false } catch (e) { this.toast('error', this.errorText(e, 'That did not work.')) } finally { this.confirm.busy = false }
        },
      }
    },

    // ── settings ──
    async openSettings() {
      this.settingsModal = { show: true, data: null, daysBefore: '', canEdit: false, busy: false, error: null }
      try {
        const { data } = await axios.get('/api/bnpl/settings')
        this.settingsModal.data = data
        this.settingsModal.daysBefore = (data.remind_days_before || []).join(', ')
        this.settingsModal.canEdit = !!data.can_edit
      } catch (e) {
        this.settingsModal.error = 'Could not load settings.'
      }
    },
    insertPlaceholder(p) {
      const d = this.settingsModal.data
      d.reminder_template = ((d.reminder_template || '') + ' ' + p).trim()
    },
    async saveSettings() {
      const m = this.settingsModal
      if (!m.canEdit) { m.show = false; return }
      m.busy = true
      m.error = null
      try {
        const days = m.daysBefore.split(',').map(s => parseInt(s, 10)).filter(n => n > 0)
        const d = m.data
        await axios.put('/api/bnpl/settings', {
          reminders_enabled: d.reminders_enabled, remind_days_before: days, remind_on_due_day: d.remind_on_due_day,
          overdue_every_days: d.overdue_every_days, max_reminders: d.max_reminders, min_hours_between: d.min_hours_between,
          grace_days: d.grace_days, default_term_days: d.default_term_days, reminder_template: d.reminder_template || null,
        })
        m.show = false
        this.toast('success', 'BNPL settings saved.')
        if (this.canViewAll) this.loadSummary()
        this.reload()
      } catch (e) {
        m.error = this.errorText(e, 'Could not save settings.')
      } finally {
        m.busy = false
      }
    },

    // ── helpers ──
    statusLabel(a) {
      if (a.status === 'overdue') return a.days_overdue ? `Overdue ${a.days_overdue}d` : 'Overdue'
      if (a.status === 'due_today') return 'Due today'
      if (a.status === 'due_soon') return 'Due in ' + Math.max(1, moment(a.due_date).startOf('day').diff(moment().startOf('day'), 'days')) + 'd'
      if (a.status === 'paid') return 'Paid'
      return a.due_date ? 'Open' : 'No due date'
    },
    statusClass(a) {
      return { overdue: 'badge-danger', due_today: 'badge-primary', due_soon: 'badge-primary', paid: 'badge-secondary' }[a.status] || 'badge-dark'
    },
    paidPct(a) { return a.total > 0 ? Math.min(100, Math.round(a.paid / a.total * 100)) : 0 },
    errorText(e, fallback) {
      const errs = e.response?.data?.errors
      return errs ? Object.values(errs).flat()[0] : (e.response?.data?.message || fallback)
    },
    toast(type, message) { this.$toasts && this.$toasts.push({ type, message }) },
    money(v) { return 'KES ' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) },
    qty(v) { return Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 }) },
    fmtDate(d) { return d ? moment(d).format('D MMM YYYY') : '—' },
    fmtIso(d) { return moment(d).format('YYYY-MM-DD') },
    isToday(d) { return moment(d).isSame(moment(), 'day') },
    dateParam(r) { return r && r.startDate && !this.isToday(r.startDate) ? moment(r.startDate).format('YYYY-MM-DD') : null },
    fmtShort(d) { return d ? moment(d).format('D MMM') : '' },
    fmtDateTime(d) { return d ? moment(d).format('D MMM YYYY, HH:mm') : '—' },
    fromNow(d) { return moment(d).fromNow() },
  },
}
</script>

